import * as React from 'react'
import { MouseEventHandler, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import { Button } from '@atoms'

import { useAnalyticsFiltersStore } from '../../../../features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'

import { Dropdown } from '../../Dropdown/Dropdown'

import { FilterProps } from '../types'
import { FilterButton } from './FilterButton'
import { WidgetSpinner } from '../../Spinner/Spinner'
import { SearchField } from '../../Form'
import { useDropdownPosition } from '../../Dropdown/useDropdownPosition'
import { useDropdownClickOutside } from '../../Dropdown/useDropdownClickOutside'

export const Filter: React.FC<FilterProps> = (props) => {
    const {
        children,
        showReset,
        saveLabel,
        resetLabel,
        disabled,
        notAvailable,
        valueLabel,
        onValueSave,
        onValueReset,
        resetState,
        isLoading,
        isError,
        options,
        searchConfig,
        scrollable,
        saveDisabled,
        containerRef,
        className,
    } = props

    const [isOpen, setIsOpen] = useState(false)
    const [showDivider, setShowDivider] = useState(false)

    // LineChart tooltip makes datepicker autofocus after selecting a date
    // so we need to hide the tooltip when filters are open
    const setAreFiltersOpen = useAnalyticsFiltersStore(
        (state) => state.setAreFiltersOpen
    )

    const { buttonRef, dropdownRef } = useDropdownClickOutside(() => {
        if (isOpen) {
            resetState()
            setIsOpen(false)
            setAreFiltersOpen(false)
        }
    })

    const handleSaveClick = useCallback(() => {
        onValueSave()
        setIsOpen(false)
        setAreFiltersOpen(false)
    }, [onValueSave, setAreFiltersOpen])

    const handleResetClick = useCallback(() => {
        onValueReset()
        setIsOpen(false)
        setAreFiltersOpen(false)
    }, [onValueReset, setAreFiltersOpen])

    const onToggleClick = useCallback<MouseEventHandler>(() => {
        setIsOpen((isOpen) => {
            if (isOpen) {
                resetState()
            }
            setAreFiltersOpen(!isOpen)
            return !isOpen
        })
    }, [resetState, setAreFiltersOpen])

    const measureDropdownContentHeight = (
        dropdownContentNode: HTMLDivElement | null
    ) => {
        if (dropdownContentNode) {
            const visibleHeight = Math.ceil(
                dropdownContentNode.getBoundingClientRect().height
            )
            const totalHeight = Math.ceil(dropdownContentNode.scrollHeight)
            const value = totalHeight > visibleHeight

            setShowDivider(value)
        }
    }

    // align dropdown content to bottom right if not enough space
    const { horizontalAlign } = useDropdownPosition(containerRef, dropdownRef, [
        isOpen,
    ])

    return (
        <Dropdown
            name={props.name}
            horizontalAlign={horizontalAlign}
            buttonProps={{
                name: `${props.name}-toggle`,
                label: props.label,
                valueLabel: valueLabel,
                disabled: disabled || notAvailable,
                buttonRef: buttonRef,
                className: className,
            }}
            buttonComponent={FilterButton}
            isOpen={isOpen}
            isError={isError}
            dropdownClassname="min-w-[288px]"
            dropdownRef={dropdownRef}
            wrapperClassname={isOpen ? 'relative z-20' : undefined}
            onClick={onToggleClick}
        >
            <div className="grid w-filterContentMinWidth grid-rows-[1fr_auto]">
                {searchConfig && (
                    <SearchField
                        {...searchConfig}
                        disabled={!!isError}
                        className="mx-4 mt-4"
                    />
                )}

                <FilterContentGuard
                    isError={!!isError}
                    isLoading={!!isLoading}
                    noResultsFound={!!options && options.length === 0}
                    dropdownContentRef={measureDropdownContentHeight}
                    scrollable={scrollable}
                >
                    {children}
                </FilterContentGuard>

                <FilterContentFooter
                    showDivider={showDivider}
                    saveLabel={saveLabel}
                    saveDisabled={saveDisabled}
                    onSave={handleSaveClick}
                    resetLabel={resetLabel}
                    onReset={handleResetClick}
                    showReset={showReset}
                />
            </div>
        </Dropdown>
    )
}

type FilterContentFooterProps = {
    saveLabel: string
    resetLabel: string
    showDivider: boolean
    showReset?: boolean
    saveDisabled?: boolean
    onReset?: () => void
    onSave?: () => void
}

const FilterContentFooter: React.FC<FilterContentFooterProps> = (props) => {
    const {
        showReset,
        onReset,
        onSave,
        resetLabel,
        saveLabel,
        showDivider,
        saveDisabled,
    } = props

    const wrapperClassname = classnames(
        'flex justify-between px-4 pb-4 gap-6',
        {
            'border-t border-t-neutral-200 pt-4': showDivider,
            'pt-2': !showDivider,
        }
    )

    return (
        <div className={wrapperClassname}>
            {showReset && (
                <Button
                    name="reset"
                    variant="ghostSecondary"
                    size="large"
                    className="grow"
                    onClick={onReset}
                >
                    {resetLabel}
                </Button>
            )}
            <Button
                name="save"
                size="large"
                onClick={onSave}
                className="grow"
                disabled={saveDisabled}
            >
                {saveLabel}
            </Button>
        </div>
    )
}

const FilterContentGuard = ({
    noResultsFound,
    isLoading,
    isError,
    children,
    dropdownContentRef,
    scrollable = true,
}: {
    isLoading: boolean
    isError: boolean
    noResultsFound: boolean
    children: React.ReactNode
    dropdownContentRef: (dropdownContentNode: HTMLDivElement | null) => void
    scrollable?: boolean
}) => {
    const { t } = useTranslation('translations')

    if (isLoading) {
        return (
            <div
                data-testid="loading-filter-content"
                className="flex h-filterContentMinHeight place-items-center"
            >
                <WidgetSpinner />
            </div>
        )
    }

    if (isError) {
        return (
            <div
                data-testid="filter-error-content"
                className="flex h-filterContentMinHeight items-center justify-center"
            >
                <p className="mx-auto max-w-48 text-center text-sm font-normal leading-6">
                    {t(`filters.isError`)}
                </p>
            </div>
        )
    }

    if (noResultsFound) {
        return (
            <div
                data-testid="no-result-found-content"
                className="flex h-filterContentMinHeight items-center justify-center"
            >
                <p className="text-center text-sm font-normal leading-6">
                    {t(`filters.noResultFound`)}
                </p>
            </div>
        )
    }

    const dropdownContentClassname = classnames('pt-2', {
        'max-h-filterContentMinHeight overflow-y-scroll': scrollable,
    })

    return (
        <div ref={dropdownContentRef} className={dropdownContentClassname}>
            {children}
        </div>
    )
}
